import { ReactElement } from 'react';

import { InputLabel, InputLabelRow } from '@/ux-common/components/input/input';
import { InputLabelInfo } from '@/ux-common/components/input/input-label-info';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';

interface SettingFieldLabelRowProps {
  /** Shown in the ⓘ popover under the name. */
  description?: string;
  /** The setting's name, and the label the control is named by. */
  label?: string;
}

/**
 * The label row shared by the editable Settings fields — the setting's name, and
 * an ⓘ whose popover repeats the name with the setting's description.
 *
 * Named for the row it renders rather than `SettingFieldLabel`, which is already
 * taken by the read-only value display registered as `DisplayRefDisplayType.Label`.
 * The two take the same props, so the collision would have type-checked.
 *
 * It exists because the three editable field types each had their own copy and the
 * copies drifted: the dropdown's popover was `max-w-64` while the boolean and text
 * fields used `max-w-96`, so the same description wrapped differently depending on
 * which control happened to render it. They now read the same at `max-w-96`, the
 * width two of the three already used. The drift predates this PR — both widths are
 * on `main`.
 *
 * `infoTabStop={false}` is deliberate and shared: the Settings page is a long grid
 * of fields, and a tab stop on every ⓘ would double the tab count between controls.
 *
 * The `data-component` deliberately replaces the registry's `InputLabelRow` marker
 * rather than adding a wrapper element — `.input-label-row` is a direct child of the
 * input group, and an extra div between them would change that structure.
 */
export const SettingFieldLabelRow = ({
  description,
  label,
}: SettingFieldLabelRowProps): ReactElement => (
  <InputLabelRow data-component="SettingFieldLabelRow">
    <InputLabel>{label}</InputLabel>
    <InputLabelInfo
      info={
        <Stack className="max-w-96">
          <Typography as="h4" className="text-header-card">
            {label}
          </Typography>
          <Typography as="p" className="text-body-base text-ckds-text-fg-tier2">
            {description}
          </Typography>
        </Stack>
      }
      infoTabStop={false}
    />
  </InputLabelRow>
);
